/**
 * EWSCustomerDrawer - Customer detail drawer
 *
 * Right-side drawer displaying detailed risk information for a selected customer.
 * Shows risk score donut, key metrics, and tabbed sections for Risk Factors,
 * Salary, EMI, and SIP data.
 */

import React, { useState, useMemo } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Loader,
  TabSwitcher,
  Table,
  type TableColumn,
} from 'equal-ds-ui';
import { FileText, Copy, Check, Wallet, ArrowLeft } from 'lucide-react';
import { EWSCustomerDrawerProps, formatINR, PaymentWindow } from '../../../types/ews.types';
import { EWSRiskScoreDonut } from './EWSRiskScoreDonut';
import { EWSRiskFactorsList } from './EWSRiskFactorsList';
import { EWSMetricCardSmall } from './EWSMetricCard';

// Import EWS icons
import ewsAverageBalanceIcon from '../../../assets/images/ews_average_balance_card_icon.png';
import ewsEmiIcon from '../../../assets/images/ews_emi_card_icon.png';
import ewsSipIcon from '../../../assets/images/ews_sip_card_icon.png';
import ewsFoirIcon from '../../../assets/images/ews_foir_card_icon.png';

/**
 * Tab identifiers for drawer sections.
 */
type DrawerTab = 'riskFactors' | 'salary' | 'emi' | 'sip';

/**
 * Salary card component for displaying individual salary entries.
 */
const SalaryCard: React.FC<{ salary: PaymentWindow; index: number }> = ({ salary, index }) => {
  const confidencePercent = Math.round(salary.confidence * 100);

  return (
    <div
      className="relative rounded-2xl overflow-hidden p-4"
      style={{
        background: 'linear-gradient(135deg, rgba(241, 245, 249, 0.8) 0%, rgba(255, 255, 255, 1) 100%)',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03)',
        border: '1px solid rgba(0, 0, 0, 0.04)',
      }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-50">
            <Wallet className="w-4 h-4 text-green-600" />
          </div>
          <span className="text-sm font-medium text-gray-700">Salary #{index + 1}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-600">Confidence:</span>
          <span className={`text-xs font-medium ${confidencePercent >= 70 ? 'text-green-600' : confidencePercent >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
            {confidencePercent}%
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Average Amount</span>
          <span className="text-lg font-bold text-gray-900">{formatINR(salary.averageAmount)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Payment Window</span>
          <span className="text-sm font-medium text-gray-700">Day {salary.startDay} - Day {salary.endDay}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Counterparty</span>
          <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]" title={salary.counterparty}>
            {salary.counterparty || '-'}
          </span>
        </div>
        {salary.category && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Category</span>
            <span className="text-sm font-medium text-gray-700">{salary.category}</span>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Empty state component for tabs with no data.
 */
const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-center justify-center py-12">
    <span className="text-gray-500">{message}</span>
  </div>
);

export const EWSCustomerDrawer: React.FC<EWSCustomerDrawerProps> = ({
  isOpen,
  onClose,
  customer,
  isLoading = false,
  onBack,
}) => {
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<DrawerTab>('riskFactors');

  // Determine which tabs to show based on available data
  const availableTabs = useMemo(() => {
    if (!customer) return [];

    const tabs: { id: DrawerTab; label: string }[] = [];

    // Always show Risk Factors if available
    if (customer.riskFactors && customer.riskFactors.length > 0) {
      tabs.push({ id: 'riskFactors', label: 'Risk Factors' });
    }

    // Show Salary tab if salaryWindow has data
    if (customer.salaryWindow && customer.salaryWindow.length > 0) {
      tabs.push({ id: 'salary', label: 'Salary' });
    }

    // Show EMI tab if emiWindow has data
    if (customer.emiWindow && customer.emiWindow.length > 0) {
      tabs.push({ id: 'emi', label: 'EMI' });
    }

    // Show SIP tab if sipWindow has data
    if (customer.sipWindow && customer.sipWindow.length > 0) {
      tabs.push({ id: 'sip', label: 'SIP' });
    }

    return tabs;
  }, [customer]);

  // Reset to first available tab when customer changes
  React.useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.find(t => t.id === activeTab)) {
      setActiveTab(availableTabs[0].id);
    }
  }, [availableTabs, activeTab]);

  // EMI table columns
  const emiColumns: TableColumn<PaymentWindow>[] = useMemo(() => [
    {
      key: 'counterparty',
      header: 'COUNTERPARTY',
      width: 200,
      accessor: (row) => row.counterparty || '-',
    },
    {
      key: 'averageAmount',
      header: 'AMOUNT',
      width: 140,
      accessor: (row) => formatINR(row.averageAmount),
    },
    {
      key: 'window',
      header: 'PAYMENT WINDOW',
      width: 140,
      render: (_value, row) => `Day ${row.startDay} - ${row.endDay}`,
    },
    {
      key: 'confidence',
      header: 'CONFIDENCE',
      width: 100,
      render: (_value, row) => {
        const percent = Math.round(row.confidence * 100);
        const colorClass = percent >= 70 ? 'text-green-600' : percent >= 40 ? 'text-amber-600' : 'text-red-600';
        return <span className={`font-medium ${colorClass}`}>{percent}%</span>;
      },
    },
    {
      key: 'category',
      header: 'CATEGORY',
      width: 120,
      accessor: (row) => row.category || '-',
    },
  ], []);

  // SIP table columns
  const sipColumns: TableColumn<PaymentWindow>[] = useMemo(() => [
    {
      key: 'counterparty',
      header: 'COUNTERPARTY',
      width: 200,
      accessor: (row) => row.counterparty || '-',
    },
    {
      key: 'averageAmount',
      header: 'AMOUNT',
      width: 140,
      accessor: (row) => formatINR(row.averageAmount),
    },
    {
      key: 'window',
      header: 'PAYMENT WINDOW',
      width: 140,
      render: (_value, row) => `Day ${row.startDay} - ${row.endDay}`,
    },
    {
      key: 'confidence',
      header: 'CONFIDENCE',
      width: 100,
      render: (_value, row) => {
        const percent = Math.round(row.confidence * 100);
        const colorClass = percent >= 70 ? 'text-green-600' : percent >= 40 ? 'text-amber-600' : 'text-red-600';
        return <span className={`font-medium ${colorClass}`}>{percent}%</span>;
      },
    },
    {
      key: 'category',
      header: 'CATEGORY',
      width: 120,
      accessor: (row) => row.category || '-',
    },
  ], []);

  // Early return if not open
  if (!isOpen) {
    return null;
  }

  // Get the consent ID (use trackingId from API, fallback to consentId for compatibility)
  const consentId = customer?.trackingId || customer?.consentId || '';

  const handleCopyConsentId = async () => {
    if (!consentId) return;
    try {
      await navigator.clipboard.writeText(consentId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId as DrawerTab);
  };

  /**
   * Render tab content based on active tab.
   */
  const renderTabContent = () => {
    if (!customer) return null;

    switch (activeTab) {
      case 'riskFactors':
        return <EWSRiskFactorsList factors={customer.riskFactors} />;

      case 'salary':
        if (!customer.salaryWindow || customer.salaryWindow.length === 0) {
          return <EmptyState message="No salary data available" />;
        }
        return (
          <div className="flex flex-col gap-4">
            {customer.salaryWindow.map((salary, index) => (
              <SalaryCard key={index} salary={salary} index={index} />
            ))}
          </div>
        );

      case 'emi':
        if (!customer.emiWindow || customer.emiWindow.length === 0) {
          return <EmptyState message="No EMI data available" />;
        }
        return (
          <div className="equal-ds-root">
            <Table<PaymentWindow>
              className="shadow-sm rounded-xl"
              data={customer.emiWindow}
              columns={emiColumns}
            />
          </div>
        );

      case 'sip':
        if (!customer.sipWindow || customer.sipWindow.length === 0) {
          return <EmptyState message="No SIP data available" />;
        }
        return (
          <div className="equal-ds-root">
            <Table<PaymentWindow>
              className="shadow-sm rounded-xl"
              data={customer.sipWindow}
              columns={sipColumns}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <DrawerOverlay />
      <Drawer
        variant="overlay"
        width="60%"
        className="border-border-default bg-gray-100"
      >
        {/* Custom Header with Consent ID */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {/* Back button - shown when opened from indicator drawer */}
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Back to list"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">CONSENT ID</span>
                <span className="text-sm text-gray-900 font-mono">
                  {consentId ? `${consentId.substring(0, 20)}...` : '—'}
                </span>
                {consentId && (
                  <button
                    onClick={handleCopyConsentId}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Copy Consent ID"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors text-xl font-light"
          >
            →|
          </button>
        </div>

        <DrawerContent className="flex flex-col flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader size={72} cellSize={20} />
            </div>
          ) : !customer ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Customer not found</div>
            </div>
          ) : (
            <>
              {/* Top Section: Risk Score + Key Metrics */}
              <div className="flex gap-6 mb-6">
                {/* Risk Score Donut */}
                <div className="flex-shrink-0">
                  <EWSRiskScoreDonut
                    score={customer.riskScore ?? 0}
                    riskLevel={customer.riskLevel}
                    size={140}
                  />
                </div>

                {/* Key Metrics Grid */}
                <div className="flex-1 grid grid-cols-2 gap-1.5">
                  <EWSMetricCardSmall
                    title="Average Balance"
                    value={formatINR(customer.averageBalance)}
                    showInfo
                    backgroundImage={ewsAverageBalanceIcon}
                  />
                  <EWSMetricCardSmall
                    title="EMI Count"
                    value={customer.activeEMIsCount}
                    showInfo
                    backgroundImage={ewsEmiIcon}
                  />
                  <EWSMetricCardSmall
                    title="SIPs"
                    value={customer.activeSIPs}
                    showInfo
                    backgroundImage={ewsSipIcon}
                  />
                  <EWSMetricCardSmall
                    title="FOIR"
                    value={customer.foir?.toFixed(2) ?? '-'}
                    showInfo
                    backgroundImage={ewsFoirIcon}
                  />
                </div>
              </div>

              {/* Tab Section */}
              {availableTabs.length > 0 && (
                <div className="space-y-4">
                  <TabSwitcher
                    tabs={availableTabs}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                    variant="horizontal"
                  />

                  {/* Tab Content */}
                  <div className="mt-4">
                    {renderTabContent()}
                  </div>
                </div>
              )}

              {/* Fallback if no tabs available */}
              {availableTabs.length === 0 && (
                <div className="flex items-center justify-center py-12">
                  <span className="text-gray-500">No additional data available for this customer</span>
                </div>
              )}
            </>
          )}
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default EWSCustomerDrawer;
